import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Calendar, Views, type View, dateFnsLocalizer } from 'react-big-calendar'
import {
  format,
  parse,
  startOfWeek,
  getDay,
  startOfMonth,
  addDays,
  addWeeks,
  addMonths,
  subDays,
  subWeeks,
  subMonths,
  isSameDay,
  isAfter,
  isToday,
  isTomorrow,
} from 'date-fns'
import { enUS } from 'date-fns/locale'
import Loader from '../components/Loading'
import ContentHeader from '../components/ContentHeader'
import CalendarDaySchedule from '../components/CalendarDaySchedule'
import CalendarWeekGrid from '../components/CalendarWeekGrid'
import MobileMonthScroller from '../components/MobileMonthScroller'
import CalendarEventPopover from '../components/CalendarEventPopover'
import CalendarKeySidebar from '../components/calendar/CalendarKeySidebar'
import ConversationContactPicker, { type ContactOption } from '../components/ConversationContactPicker'
import { getSessionAuth } from '../utils/sessionAuth'
import { ensureSessionUserId, getSessionUserId } from '../utils/sessionUser'
import {
  buildEventMap,
  parseCalendarItemDates,
  toDisplayEvents,
  type CalendarEventDisplay,
  type CalendarItem,
  type CalendarItemWithDate,
} from '../utils/calendarDates'
import { getEventStyle } from '../utils/calendarEventColors'
import {
  CalendarConnectionRequiredError,
  buildOAuthStartUrl,
  fetchCalendarEvents,
  fetchCalendarEventClients,
  fetchMyConnections,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  type CalendarConnection,
  type CalendarServiceEvent,
} from '../api/crm'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import '../style/views/CalendarView.css'

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { locale: enUS }),
  getDay,
  locales: { 'en-US': enUS },
})
const filterToView: Record<'Month' | 'Week' | 'Day', View> = {
  Month: Views.MONTH,
  Week: Views.WEEK,
  Day: Views.DAY,
}
const calendarFormats = {
  dayFormat: (date: Date, culture?: string, loc?: { format: (d: Date, f: string, c?: string) => string }) =>
    loc?.format(date, 'EEE d', culture) ?? format(date, 'EEE d'),
}
const WeekHeader = ({ date }: { date: Date }) => (
  <div className="calendar-week-header">
    <span className="calendar-week-header-day">{format(date, 'EEE').toUpperCase()}</span>
    <span className="calendar-week-header-date">{format(date, 'd')}</span>
  </div>
)
const calendarComponents = {
  week: { header: WeekHeader },
}
type AgendaSectionProps = { items: { id: string; start: Date; title: string }[] }
function AgendaSection({ items }: AgendaSectionProps) {
  return (
    <div className="calendar-key-agenda">
      <div className="calendar-key-agenda-title">Today</div>
      {items.length ? (
        <div className="calendar-key-agenda-list">
          {items.map((event) => (
            <div
              key={`agenda-${event.id}`}
              className="calendar-key-agenda-item"
              data-is-today
            >
              <span className="calendar-key-agenda-time">{format(event.start, 'h:mm a').replace('am', 'AM').replace('pm', 'PM')}</span>
              <span className="calendar-key-agenda-title-text">{event.title}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="calendar-key-agenda-empty">No events today.</div>
      )}
    </div>
  )
}

type UpcomingEventsSectionProps = {
  events: CalendarEventDisplay[]
  onSelect: (event: CalendarEventDisplay) => void
}

function UpcomingEventsSection({ events, onSelect }: UpcomingEventsSectionProps) {
  return (
    <div className="calendar-key-agenda calendar-key-upcoming">
      <div className="calendar-key-agenda-title">Coming up</div>
      {events.length ? (
        <div className="calendar-key-agenda-list">
          {events.map((event) => (
            <button
              type="button"
              key={`upcoming-${event.id}`}
              className="calendar-key-agenda-item calendar-key-upcoming-item"
              onClick={() => onSelect(event)}
            >
              <span className="calendar-key-agenda-time">
                {isToday(event.start)
                  ? `Today ${format(event.start, 'h:mm a').replace('am', 'AM').replace('pm', 'PM')}`
                  : isTomorrow(event.start)
                    ? `Tomorrow ${format(event.start, 'h:mm a').replace('am', 'AM').replace('pm', 'PM')}`
                    : `${format(event.start, 'EEE MMM d, h:mm a').replace('am', 'AM').replace('pm', 'PM')}`}
              </span>
              <span className="calendar-key-agenda-title-text">{event.title}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="calendar-key-agenda-empty">No upcoming events.</div>
      )}
    </div>
  )
}

const CALENDAR_FILTER_STORAGE_KEY = 'calendarActiveFilter'
const CALENDAR_COLOR_MODE_STORAGE_KEY = 'calendarColorModeEnabled'
const isCalendarFilter = (value: string | null): value is 'Month' | 'Week' | 'Day' =>
  value === 'Month' || value === 'Week' || value === 'Day'

function CalendarView() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [events, setEvents] = useState<CalendarItem[]>([])
  const [connections, setConnections] = useState<CalendarConnection[]>([])
  const [loading, setLoading] = useState(true)
  const [userId, setUserId] = useState<string | null>(() => getSessionUserId())
  const [activeFilter, setActiveFilter] = useState<'Month' | 'Week' | 'Day'>(() => {
    if (typeof window === 'undefined') return 'Month'
    const stored = window.localStorage.getItem(CALENDAR_FILTER_STORAGE_KEY)
    return isCalendarFilter(stored) ? stored : 'Month'
  })
  const [currentDate, setCurrentDate] = useState<Date>(new Date())
  const [isMobile, setIsMobile] = useState(false)
  const [dayViewDate, setDayViewDate] = useState<Date | null>(null)
  const [focusId, setFocusId] = useState<string | null>(null)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [draftEvent, setDraftEvent] = useState<CalendarItemWithDate | null>(null)
  const [tailLeft, setTailLeft] = useState('50%')
  const [tailPlacement, setTailPlacement] = useState<'top' | 'bottom'>('bottom')
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [anchorCenter, setAnchorCenter] = useState<{ x: number; y: number } | null>(null)
  const [modalStyle, setModalStyle] = useState<{ top: number; left: number } | null>(null)
  const modalRef = useRef<HTMLDivElement | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [archiveError, setArchiveError] = useState<string | null>(null)
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; date: Date } | null>(null)
  const [draftTitle, setDraftTitle] = useState('New event')
  const [draftLocation, setDraftLocation] = useState('')
  const [dateClients, setDateClients] = useState<Map<string, ContactOption[]>>(new Map())
  const [showContactPicker, setShowContactPicker] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)
  const [colorModeOn, setColorModeOn] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(CALENDAR_COLOR_MODE_STORAGE_KEY) === 'true'
  })
  const [showKey, setShowKey] = useState(() => {
    if (typeof window === 'undefined') return true
    const stored = window.localStorage.getItem('calendarKeyOpen')
    if (stored == null) return true
    return stored === 'true'
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem('calendarKeyOpen', showKey ? 'true' : 'false')
  }, [showKey])
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(CALENDAR_FILTER_STORAGE_KEY, activeFilter)
  }, [activeFilter])
  useEffect(() => {
    if (typeof window === 'undefined') return
    const syncColorMode = () => setColorModeOn(window.localStorage.getItem(CALENDAR_COLOR_MODE_STORAGE_KEY) === 'true')
    syncColorMode()
    window.addEventListener('focus', syncColorMode)
    document.addEventListener('visibilitychange', syncColorMode)
    window.addEventListener('storage', syncColorMode)
    return () => {
      window.removeEventListener('focus', syncColorMode)
      document.removeEventListener('visibilitychange', syncColorMode)
      window.removeEventListener('storage', syncColorMode)
    }
  }, [])
  const isDraft = !!draftEvent && draftEvent.id === 'new'
  useEffect(() => {
    if (draftEvent) {
      setDraftTitle(draftEvent.title ?? '')
      setDraftLocation(draftEvent.location ?? '')
    } else {
      setDraftTitle('New event')
      setDraftLocation('')
    }
  }, [draftEvent?.title, draftEvent?.location])

  useEffect(() => {
    const syncSize = () => setIsMobile(window.innerWidth <= 768)
    syncSize()
    window.addEventListener('resize', syncSize)
    return () => window.removeEventListener('resize', syncSize)
  }, [])
  useEffect(() => {
    if (!isMobile) return
    if (activeFilter === 'Week') setActiveFilter('Month')
  }, [isMobile, activeFilter])

  useEffect(() => {
    const focus = searchParams.get('focus')
    if (focus && focus !== focusId) {
      setFocusId(focus)
    }
  }, [searchParams, focusId])

  useEffect(() => {
    const view = searchParams.get('view')
    if (isCalendarFilter(view) && view !== activeFilter) {
      setActiveFilter(view)
      if (view === 'Day') {
        const today = new Date()
        setCurrentDate(today)
        setDayViewDate(today)
      }
    }
  }, [searchParams, activeFilter])

  const updatePositionFromAnchor = useCallback(() => {
    const rect = anchorEl?.getBoundingClientRect()
    const anchorX = rect ? rect.left + rect.width / 2 : anchorCenter?.x
    const anchorTop = rect ? rect.top + window.scrollY : anchorCenter?.y
    const anchorBottom = rect ? rect.bottom + window.scrollY : anchorCenter?.y
    if (anchorX == null || anchorTop == null || anchorBottom == null) return
    const padding = 16
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800
    const viewportTop = window.scrollY + padding
    const viewportBottom = window.scrollY + viewportHeight - padding
    const width = modalRef.current?.offsetWidth || 500
    const height = modalRef.current?.offsetHeight || 260
    let left = anchorX - width / 2
    left = Math.max(padding, Math.min(left, viewportWidth - width - padding))
    const tailPx = Math.max(20, Math.min(width - 20, anchorX - left))
    const gapAbove = 96
    const gapBelow = 14
    const aboveTop = anchorTop - height - gapAbove
    const belowTop = anchorBottom + gapBelow
    const spaceAbove = anchorTop - viewportTop
    const spaceBelow = viewportBottom - anchorBottom
    let placement: 'top' | 'bottom' = 'bottom'
    let top = aboveTop
    if (aboveTop < viewportTop && spaceBelow > spaceAbove) {
      placement = 'top'
      top = belowTop
    } else if (belowTop + height > viewportBottom && spaceAbove >= spaceBelow) {
      placement = 'bottom'
      top = aboveTop
    } else if (belowTop + height <= viewportBottom && aboveTop < viewportTop) {
      placement = 'top'
      top = belowTop
    }
    top = Math.max(viewportTop, Math.min(top, viewportBottom - height))
    setModalStyle({ top, left })
    setTailLeft(`${tailPx}px`)
    setTailPlacement(placement)
  }, [anchorEl, anchorCenter])

  const parsedEvents: CalendarItemWithDate[] = useMemo(() => {
    const mapped = parseCalendarItemDates(events)

    if (focusId) {
      const focused = mapped.find((ev) => ev.id === focusId)
      if (focused?.parsedStart) {
        setCurrentDate(focused.parsedStart)
        setDayViewDate(focused.parsedStart)
        setActiveFilter('Day')
        setTimeout(() => {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            next.delete('focus')
            return next
          })
        }, 0)
      }
    }

    return mapped
  }, [events, focusId, setSearchParams])

  const parsedEventById = useMemo(() => {
    const map = new Map<string, CalendarItemWithDate>()
    parsedEvents.forEach((ev) => map.set(ev.id, ev))
    return map
  }, [parsedEvents])

  // Handle Hash for Command Palette / Global Search deep linking
  useEffect(() => {
    if (loading) return
    
    const hashId = window.location.hash.replace('#', '')
    if (!hashId) return

    if (hashId === 'new') {
      const start = new Date()
      start.setHours(12, 0, 0, 0)
      const end = new Date(start.getTime() + 60 * 60 * 1000)
      const startIso = start.toISOString()
      const endIso = end.toISOString()

      setCurrentDate(start)
      setDayViewDate(null)
      setActiveFilter('Month')

      setDateClients((prev) => {
        const next = new Map(prev)
        next.set('new', [])
        return next
      })

      setDraftEvent({
        id: 'new',
        title: 'New event',
        datetime_start: startIso,
        datetime_end: endIso,
        date_created: startIso,
        location: '',
        parsedStart: start,
        parsedEnd: end,
      })

      setSelectedEventId('new')
      setAnchorEl(null)
      setAnchorCenter({
        x: window.innerWidth / 2,
        y: window.scrollY + 200
      })

      window.history.replaceState(null, '', window.location.pathname + window.location.search)
      requestAnimationFrame(() => updatePositionFromAnchor())
      return
    }

    if (events.length === 0) return
    
    if (hashId && hashId !== selectedEventId) {
      const targetEvent = parsedEventById.get(hashId)
      if (targetEvent && targetEvent.parsedStart) {
        // Move to the correct day
        setCurrentDate(targetEvent.parsedStart)
        setDayViewDate(targetEvent.parsedStart)
        
        // Open the popover
        setSelectedEventId(hashId)
        setDraftEvent(null)
        setDraftTitle(targetEvent.title || '')
        
        // Use centered anchor as fallback for hash-navigated events
        setAnchorEl(null)
        setAnchorCenter({
          x: window.innerWidth / 2,
          y: window.innerHeight / 2 + window.scrollY - 100
        })
        
        // Clear the hash so it doesn't trigger again on every load
        window.history.replaceState(null, '', window.location.pathname + window.location.search)
        
        requestAnimationFrame(() => updatePositionFromAnchor())
      }
    }
  }, [loading, events, parsedEventById, selectedEventId, updatePositionFromAnchor])

  useEffect(() => {
    if (!selectedEventId) return
    const raf = requestAnimationFrame(() => updatePositionFromAnchor())
    const handler = () => updatePositionFromAnchor()
    window.addEventListener('scroll', handler, true)
    window.addEventListener('resize', handler)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', handler, true)
      window.removeEventListener('resize', handler)
    }
  }, [selectedEventId, updatePositionFromAnchor])

  const loadCalendarList = useCallback(
    async (_ensuredId: string, signal?: AbortSignal) => {
      const fetchedConnections = await fetchMyConnections(signal)
        setConnections(fetchedConnections)

        let fetchedEvents: CalendarServiceEvent[] = []
        try {
          fetchedEvents = await fetchCalendarEvents({}, signal)
          setConnectError(null)
        } catch (err) {
          if (err instanceof CalendarConnectionRequiredError) {
            setEvents([])
            setConnectError(null)
            return
          }
          throw err
        }

        // Map calendar-service shape → CalendarItem shape the rest of the component uses
        const mapped = fetchedEvents.map((e: CalendarServiceEvent) => ({
          id:             e.id,
          title:          e.title,
          datetime_start: e.starts_at,
          datetime_end:   e.ends_at,
          location:       e.location ?? '',
          tags:           e.provider === 'microsoft' ? 'outlook' : (e.provider ?? null),
          all_day:        e.all_day,
        }))
        setEvents(mapped)
    },
    [],
  )

  useEffect(() => {
    const controller = new AbortController()
    const loadEvents = async () => {
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) {
        setLoading(false)
        return
      }
      if (!userId) setUserId(ensuredId)
      try {
        setLoading(true)
        await loadCalendarList(ensuredId, controller.signal)
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Failed to load calendar', err)
          setEvents([])
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    loadEvents()

    const POLL_INTERVAL_MS = 60_000
    const interval = setInterval(async () => {
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId || controller.signal.aborted) return
      try {
        await loadCalendarList(ensuredId, controller.signal)
      } catch {
        // Silent — polling failures shouldn't surface to the user
      }
    }, POLL_INTERVAL_MS)

    return () => {
      controller.abort()
      clearInterval(interval)
    }
  }, [loadCalendarList, userId])

  const selectedEvent = draftEvent || (selectedEventId ? parsedEventById.get(selectedEventId) : null)
  const selectedEventClients = selectedEvent ? dateClients.get(selectedEvent.id) || [] : []
  const canManageContacts = !!selectedEvent

  useEffect(() => {
    if (!selectedEvent) return
    setTailLeft('50%')
  }, [selectedEvent])

  useEffect(() => {
    if (!draftEvent && selectedEvent) {
      setDraftTitle(selectedEvent.title ?? '')
      setDraftLocation(selectedEvent.location ?? '')
    }
  }, [draftEvent, selectedEvent])

  const fetchDateClients = useCallback(
    async (dateId: string) => {
      try {
        const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
        if (!ensuredId) return
        const data = await fetchCalendarEventClients(dateId, ensuredId)
        if (Array.isArray(data)) {
          const mapped = data.map((c) => ({
            ...c,
            fullName: [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Unknown',
          }))
          setDateClients((prev) => {
            const next = new Map(prev)
            next.set(dateId, mapped)
            return next
          })
        }
      } catch {
        /* ignore */
      }
    },
    [userId],
  )

  useEffect(() => {
    if (selectedEvent?.id && !dateClients.has(selectedEvent.id)) {
      fetchDateClients(selectedEvent.id)
    }
  }, [selectedEvent?.id, dateClients, fetchDateClients])

  const handleCreateRequest = (start: Date, anchor: { x: number; y: number }) => {
    const end = new Date(start.getTime() + 60 * 60 * 1000)
    const startIso = start.toISOString()
    const endIso = end.toISOString()
    setDateClients((prev) => {
      const next = new Map(prev)
      next.set('new', [])
      return next
    })
    setDraftEvent({
      id: 'new',
      title: draftTitle,
      datetime_start: startIso,
      datetime_end: endIso,
      date_created: startIso,
      location: draftLocation,
      parsedStart: start,
      parsedEnd: end,
    })
    setSelectedEventId('new')
    setAnchorEl(null)
    setAnchorCenter(anchor)
    setTailLeft('50%')
    setModalStyle(null)
    setContextMenu(null)
    requestAnimationFrame(() => updatePositionFromAnchor())
  }

  const handleSaveEventCS = async (
    id: string,
    startIso: string,
    endIso: string,
    location?: string | null,
  ) => {
    try {
      setSaveError(null)
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) throw new Error('Missing user id')
      if (id === 'new' || draftEvent) {
        const activeConnection = connections.find((c) => c.status === 'active')
        if (!activeConnection) throw new Error('No active calendar connection')
        await createCalendarEvent({
          provider: activeConnection.provider,
          title: draftEvent?.title ?? draftTitle ?? 'Untitled event',
          starts_at: startIso,
          ends_at: endIso,
          location: location ?? null,
        })
        setDraftEvent(null)
        setSelectedEventId(null)
        setDateClients((prev) => {
          const next = new Map(prev)
          next.delete('new')
          return next
        })
      } else {
        const existingTitle = parsedEventById.get(id)?.title || 'Untitled event'
        const trimmedDraftTitle = draftTitle.trim()
        await updateCalendarEvent(id, {
          title: trimmedDraftTitle.length ? trimmedDraftTitle : existingTitle,
          starts_at: startIso,
          ends_at: endIso,
          location: location ?? null,
        })
        setSelectedEventId(null)
        setAnchorEl(null)
        setAnchorCenter(null)
        setModalStyle(null)
      }
      await loadCalendarList(ensuredId)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save event')
      throw err
    }
  }

  const handleDeleteEventCS = async (id: string) => {
    try {
      setArchiveError(null)
      setDraftEvent(null)
      await deleteCalendarEvent(id)
      setEvents((prev) => prev.filter((evt) => evt.id !== id))
      setSelectedEventId(null)
      setAnchorEl(null)
      setAnchorCenter(null)
      setModalStyle(null)
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Failed to delete event')
      throw err
    }
  }

  const handleSlotSelect = (slotInfo: { start: Date; end: Date; action?: string }) => {
    const action = slotInfo.action || ''
    const date = slotInfo.start
    const actionLower = action.toLowerCase()
    if (actionLower === 'select' || actionLower === 'click' || action === '') {
      setDayViewDate(date)
      setCurrentDate(date)
    }
    if (actionLower === 'doubleclick') {
      setActiveFilter('Day')
      setCurrentDate(date)
      setDayViewDate(date)
      setSelectedEventId(null)
      setDraftEvent(null)
      setAnchorEl(null)
      setAnchorCenter(null)
      setModalStyle(null)
    }
  }

  useEffect(() => {
    // Scroll locking disabled to prevent calendar layout shifting
    return;
  }, [selectedEventId, activeFilter])

  const calendarEvents = useMemo<CalendarEventDisplay[]>(
    () => toDisplayEvents(parsedEvents),
    [parsedEvents],
  )

  const eventMap = useMemo(() => buildEventMap(calendarEvents), [calendarEvents])

  const formatRangeLabel = () => {
    if (activeFilter === 'Day') return format(currentDate, 'MMMM d, yyyy')
    if (activeFilter === 'Week') {
      const weekStart = startOfWeek(currentDate, { locale: enUS })
      const weekEnd = addDays(weekStart, 6)
      return `${format(weekStart, 'MMM d')} – ${format(weekEnd, 'MMM d, yyyy')}`
    }
    return format(currentDate, 'MMMM yyyy')
  }

  const navigateByView = (direction: 1 | -1) => {
    if (activeFilter === 'Day') setCurrentDate(direction > 0 ? addDays(currentDate, 1) : subDays(currentDate, 1))
    else if (activeFilter === 'Week') setCurrentDate(direction > 0 ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1))
    else setCurrentDate(direction > 0 ? addMonths(currentDate, 1) : subMonths(currentDate, 1))
  }

  const handleFilterChange = (value: 'Month' | 'Week' | 'Day') => {
    setActiveFilter(value)
    if (value === 'Day') {
      const base = dayViewDate ?? currentDate
      setDayViewDate(base)
      setCurrentDate(base)
    } else {
      setDayViewDate(null)
    }
    if (value !== 'Month' && value !== 'Week' && value !== 'Day') {
      setSelectedEventId(null)
      setAnchorEl(null)
      setAnchorCenter(null)
      setModalStyle(null)
    }
  }

  const goToToday = () => {
    const today = new Date()
    setCurrentDate(today)
    if (activeFilter === 'Day') {
      setDayViewDate(today)
    } else {
      setDayViewDate(null)
    }
  }

  const needsCalendarConnection = connections.length === 0

  const handleConnectCalendar = (provider: 'google' | 'microsoft') => {
    try {
      setConnectError(null)
      window.location.href = buildOAuthStartUrl(provider)
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : 'Unable to start calendar connection.')
    }
  }

  const renderCalendarConnectPanel = () => (
    <div className="calendar-connect-panel">
      <div className="calendar-connect-icon" aria-hidden="true">+</div>
      <h2>Connect your calendar to see and create events</h2>
      <p>Bev will add events directly to your calendar after calls.</p>
      <div className="calendar-connect-actions">
        <button type="button" className="primary-button" onClick={() => handleConnectCalendar('google')}>
          Connect Google
        </button>
        <button type="button" className="primary-outline" onClick={() => handleConnectCalendar('microsoft')}>
          Connect Outlook
        </button>
      </div>
      {connectError ? <div className="calendar-key-error">{connectError}</div> : null}
    </div>
  )

  const calendarKeyItems = useMemo(
    () => {
      const tagsInEvents = new Set<string>()
      events.forEach((event) => {
        const tags = Array.isArray(event.tags)
          ? event.tags
          : typeof event.tags === 'string'
            ? [event.tags]
            : []
        tags.forEach((t) => tagsInEvents.add(t.toLowerCase()))
      })

      const items = [
        { label: 'Default', gradient: 'linear-gradient(135deg, #6481d2, #78f2c8)' },
        { label: 'Reminder', gradient: 'linear-gradient(135deg, #2bb673, #9ae6b4)' },
        { label: 'Meeting', gradient: 'linear-gradient(135deg, #e05858, #f6a36a)' },
      ]

      if (tagsInEvents.has('google')) {
        items.push({
          label: 'Google',
          gradient: 'radial-gradient(150% 190% at 0% 18%, rgba(66, 133, 244, 0.86) 0%, rgba(66, 133, 244, 0) 54%), radial-gradient(150% 200% at 35% 22%, rgba(234, 67, 53, 0.72) 0%, rgba(234, 67, 53, 0) 58%), radial-gradient(150% 200% at 66% 26%, rgba(251, 188, 5, 0.68) 0%, rgba(251, 188, 5, 0) 56%), radial-gradient(150% 200% at 100% 24%, rgba(52, 168, 83, 0.76) 0%, rgba(52, 168, 83, 0) 57%), linear-gradient(135deg, #78adfb 0%, #f6a79d 38%, #ffe289 68%, #6cc889 100%)'
        })
      }
      if (tagsInEvents.has('azure') || tagsInEvents.has('outlook')) {
        items.push({
          label: 'Outlook',
          gradient: 'linear-gradient(135deg, #034fa0, #0078d4, #249ee4)' // Dark -> Light Outlook Gradient
        })
      }
      
      return items
    },
    [events],
  )
  const primaryCalendarKeyItems = useMemo(
    () => calendarKeyItems.filter((item) => !['google', 'outlook'].includes(item.label.toLowerCase())),
    [calendarKeyItems],
  )
  const thirdPartyCalendarKeyItems = useMemo(
    () => calendarKeyItems.filter((item) => ['google', 'outlook'].includes(item.label.toLowerCase())),
    [calendarKeyItems],
  )

  const eventStyleGetter = useCallback((event: CalendarEventDisplay) => getEventStyle(event.tags), [])
  const resolveContextDate = useCallback(
    (clientX: number, clientY: number, target: HTMLElement | null) => {
      if (activeFilter === 'Month') {
        const monthRoot = document.querySelector<HTMLElement>('.calendar-scroll .rbc-month-view')
        if (monthRoot) {
          const dayCells = Array.from(monthRoot.querySelectorAll<HTMLElement>('.rbc-day-bg'))
          const hitCell = dayCells.find((el) => {
            const rect = el.getBoundingClientRect()
            return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
          })
          if (hitCell) {
            const cellIndex = dayCells.indexOf(hitCell)
            if (cellIndex >= 0) {
              return addDays(startOfWeek(startOfMonth(currentDate), { locale: enUS }), cellIndex)
            }
          }
        }
      }

      if (activeFilter === 'Week') {
        const weekRoot = document.querySelector<HTMLElement>('.calendar-scroll .rbc-time-view')
        if (weekRoot) {
          const dayColumns = Array.from(
            weekRoot.querySelectorAll<HTMLElement>('.rbc-time-content .rbc-day-slot'),
          )
          const hitColumn = dayColumns.find((el) => {
            const rect = el.getBoundingClientRect()
            return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
          })
          if (hitColumn) {
            const columnIndex = dayColumns.indexOf(hitColumn)
            if (columnIndex >= 0) {
              return addDays(startOfWeek(currentDate, { locale: enUS }), columnIndex)
            }
          }

          const headerCells = Array.from(
            weekRoot.querySelectorAll<HTMLElement>('.rbc-time-header-content .rbc-header'),
          )
          const headerHit = headerCells.find((el) => {
            const rect = el.getBoundingClientRect()
            return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
          })
          if (headerHit) {
            const headerIndex = headerCells.indexOf(headerHit)
            if (headerIndex >= 0) {
              return addDays(startOfWeek(currentDate, { locale: enUS }), headerIndex)
            }
          }
        }
      }

      const bgCell = target?.closest?.('.rbc-day-bg') as HTMLElement | null
      if (bgCell) {
        const monthRoot = document.querySelector<HTMLElement>('.calendar-scroll .rbc-month-view')
        if (monthRoot) {
          const dayCells = Array.from(monthRoot.querySelectorAll<HTMLElement>('.rbc-day-bg'))
          const cellIndex = dayCells.indexOf(bgCell)
          if (cellIndex >= 0) {
            return addDays(startOfWeek(startOfMonth(currentDate), { locale: enUS }), cellIndex)
          }
        }
      }

      return new Date(currentDate)
    },
    [activeFilter, currentDate],
  )
  const mobileFilterLabels = useMemo(() => ['Month', 'Day'] as const, [])
  const mobileActiveIndex = Math.max(0, mobileFilterLabels.indexOf(activeFilter as 'Month' | 'Day'))

  const todayAgenda = useMemo(() => {
    const today = new Date()
    return calendarEvents
      .filter((event) => isSameDay(event.start, today))
      .sort((a, b) => a.start.getTime() - b.start.getTime())
  }, [calendarEvents])

  const upcomingEvents = useMemo(() => {
    const now = new Date()
    return calendarEvents
      .filter((event) => isAfter(event.start, now))
      .sort((a, b) => a.start.getTime() - b.start.getTime())
      .slice(0, 7)
  }, [calendarEvents])

  const handleUpcomingClick = (event: (typeof upcomingEvents)[number]) => {
    const date = event.start
    setCurrentDate(date)
    setDayViewDate(date)
    setSelectedEventId(String(event.id))
    setAnchorEl(null)
    setAnchorCenter({ x: window.innerWidth / 2, y: window.scrollY + 200 })
    setTailLeft('50%')
    setModalStyle(null)
  }

  const agendaItems = todayAgenda

  return (
    <>
      {isMobile ? (
        <div className="calendar-header-row mobile-header">
          <h1>Calendar</h1>
          <div className="calendar-header-actions">
            <button type="button" className="primary-outline small" onClick={goToToday}>
              Today
            </button>
            <div
              className="filters"
              style={
                {
                  ['--active-index' as string]: mobileActiveIndex,
                  ['--filter-count' as string]: mobileFilterLabels.length,
                } as CSSProperties
              }
            >
              {mobileFilterLabels.map((label) => (
                <button
                  type="button"
                  key={label}
                  className={`filter ${activeFilter === label ? 'active' : ''}`}
                  onClick={() => handleFilterChange(label as typeof activeFilter)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <ContentHeader
          title="Calendar"
          filters={['Month', 'Week', 'Day']}
          activeFilter={activeFilter}
          onFilterChange={(value) => handleFilterChange(value as typeof activeFilter)}
        />
      )}
      <div className="content-panel">
        {loading ? (
          <div className="loader-wrapper">
            <Loader />
          </div>
        ) : (
          <>
            {!isMobile && activeFilter !== 'Day' && (
              <div className="calendar-layout">
                <CalendarKeySidebar
                  showKey={showKey}
                  primaryItems={primaryCalendarKeyItems}
                  thirdPartyItems={thirdPartyCalendarKeyItems}
                  agenda={<AgendaSection items={agendaItems} />}
                  upcoming={
                    <UpcomingEventsSection events={upcomingEvents} onSelect={handleUpcomingClick} />
                  }
                  onShowKey={() => setShowKey(true)}
                  onHideKey={() => setShowKey(false)}
                />
                <div className={`calendar-shell ${!colorModeOn ? 'calendar-color-off' : ''}`}>
                  <div className="calendar-controls">
                    <div className="calendar-nav">
                      <button type="button" onClick={() => navigateByView(-1)}>
                        ← Prev
                      </button>
                      <button type="button" onClick={() => setCurrentDate(new Date())}>Today</button>
                      <button type="button" onClick={() => navigateByView(1)}>
                        Next →
                      </button>
                    </div>
                    <div className="calendar-range">{formatRangeLabel()}</div>
                    <div className="calendar-nav calendar-nav-spacer" aria-hidden="true">
                      <button type="button">← Prev</button>
                      <button type="button">Today</button>
                      <button type="button">Next →</button>
                    </div>
                  </div>
                  {needsCalendarConnection ? (
                    renderCalendarConnectPanel()
                  ) : (
                    <div
                      className="calendar-scroll"
                      onMouseDown={(e) => {
                        lastPointerRef.current = { x: e.clientX, y: e.clientY + window.scrollY }
                        setContextMenu(null)
                      }}
                      onContextMenu={(e) => {
                        if (activeFilter === 'Week') return
                        e.preventDefault()
                        const target = e.target as HTMLElement | null
                        const date = resolveContextDate(e.clientX, e.clientY, target)
                        setCurrentDate(date)
                        setDayViewDate(date)
                        lastPointerRef.current = { x: e.clientX, y: e.clientY + window.scrollY }
                        setContextMenu({ x: e.clientX, y: e.clientY + window.scrollY, date })
                      }}
                    >
                      {activeFilter === 'Week' ? (
                        <CalendarWeekGrid
                          weekDate={currentDate}
                          events={calendarEvents}
                          onSelectEvent={(id, rect) => {
                            setSelectedEventId(id)
                            setDraftEvent(null)
                            setDraftTitle(parsedEventById.get(id)?.title || '')
                            setAnchorEl(null)
                            if (rect) {
                              setAnchorCenter({
                                x: rect.left + rect.width / 2,
                                y: rect.top + window.scrollY,
                              })
                            } else {
                              setAnchorCenter({
                                x: window.innerWidth / 2,
                                y: window.scrollY + 200,
                              })
                            }
                            requestAnimationFrame(() => updatePositionFromAnchor())
                          }}
                          onContextMenuSlot={(date, anchor) => {
                            setCurrentDate(date)
                            setDayViewDate(date)
                            lastPointerRef.current = { x: anchor.x, y: anchor.y }
                            setContextMenu({ x: anchor.x, y: anchor.y, date })
                          }}
                        />
                      ) : (
                        <Calendar
                          localizer={localizer}
                          formats={calendarFormats}
                          components={calendarComponents}
                          events={calendarEvents}
                          startAccessor="start"
                          endAccessor="end"
                          tooltipAccessor={() => ''}
                          eventPropGetter={eventStyleGetter}
                          style={{ height: '70vh', minHeight: '60vh' }}
                          view={Views.MONTH}
                          onView={(nextView: View) => {
                            const nextFilter = (Object.keys(filterToView) as Array<'Month' | 'Week' | 'Day'>).find(
                              (key) => filterToView[key] === nextView,
                            )
                            if (nextFilter) setActiveFilter(nextFilter)
                          }}
                          date={currentDate}
                          onNavigate={(nextDate: Date) => setCurrentDate(nextDate)}
                          toolbar={false}
                          views={[Views.MONTH]}
                          onSelectEvent={(event, e) => {
                            setSelectedEventId(String(event.id))
                            setDraftEvent(null)
                            setDraftTitle(event.title || '')
                            const rawTarget = (e as unknown as { target?: HTMLElement }).target
                            const eventEl = rawTarget?.closest?.('.rbc-event') as HTMLElement | null
                            const rect = eventEl?.getBoundingClientRect() ?? rawTarget?.getBoundingClientRect()
                            if (rect) {
                              setAnchorEl(eventEl ?? (rawTarget as HTMLElement))
                              setAnchorCenter({
                                x: rect.left + rect.width / 2,
                                y: rect.top + window.scrollY,
                              })
                            } else {
                              setAnchorEl(null)
                              setAnchorCenter({
                                x: window.innerWidth / 2,
                                y: window.scrollY + 200,
                              })
                            }
                            requestAnimationFrame(() => updatePositionFromAnchor())
                          }}
                          selectable
                          onSelectSlot={handleSlotSelect}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {!isMobile && activeFilter === 'Day' && (
              <div className="calendar-layout">
                <CalendarKeySidebar
                  showKey={showKey}
                  primaryItems={primaryCalendarKeyItems}
                  thirdPartyItems={thirdPartyCalendarKeyItems}
                  agenda={<AgendaSection items={agendaItems} />}
                  upcoming={
                    <UpcomingEventsSection events={upcomingEvents} onSelect={handleUpcomingClick} />
                  }
                  onShowKey={() => setShowKey(true)}
                  onHideKey={() => setShowKey(false)}
                />
                <div className={`calendar-shell ${!colorModeOn ? 'calendar-color-off' : ''}`}>
                  <div className="calendar-controls">
                    <div className="calendar-nav">
                      <button type="button" onClick={() => setCurrentDate(subDays(currentDate, 1))}>
                        ← Prev
                      </button>
                      <button type="button" onClick={goToToday}>Today</button>
                      <button type="button" onClick={() => setCurrentDate(addDays(currentDate, 1))}>
                        Next →
                      </button>
                    </div>
                    <div className="calendar-range">{format(currentDate, 'MMM d, yyyy')}</div>
                    <div className="calendar-nav calendar-nav-spacer" aria-hidden="true">
                      <button type="button">← Prev</button>
                      <button type="button">Today</button>
                      <button type="button">Next →</button>
                    </div>
                  </div>
                  {needsCalendarConnection ? (
                    renderCalendarConnectPanel()
                  ) : (
                    <div
                      className="calendar-scroll calendar-day-scroll"
                      onContextMenu={(e) => {
                        e.preventDefault()
                        const anchor = { x: e.clientX, y: e.clientY + window.scrollY }
                        const selectedDate = new Date(currentDate)
                        setCurrentDate(selectedDate)
                        setDayViewDate(selectedDate)
                        lastPointerRef.current = anchor
                        setContextMenu({ x: anchor.x, y: anchor.y, date: selectedDate })
                      }}
                    >
                      <CalendarDaySchedule
                        baseDate={currentDate}
                        items={calendarEvents.flatMap((evt) =>
                          isSameDay(evt.start, currentDate)
                            ? [{ id: String(evt.id), title: evt.title, start: evt.start.toISOString(), end: evt.end.toISOString(), tags: evt.tags }]
                            : []
                        )}
                        showHeader={false}
                        variant="plain"
                        onCreateRequest={handleCreateRequest}
                        onSelect={(id, rect) => {
                          setSelectedEventId(id)
                          setAnchorEl(null)
                          if (rect) {
                            setAnchorCenter({
                              x: rect.left + rect.width / 2,
                              y: rect.top + window.scrollY,
                            })
                          } else {
                            setAnchorCenter({
                              x: window.innerWidth / 2,
                              y: window.scrollY + 200,
                            })
                          }
                          requestAnimationFrame(() => updatePositionFromAnchor())
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {isMobile && needsCalendarConnection && renderCalendarConnectPanel()}

            {isMobile && !needsCalendarConnection && activeFilter === 'Month' && (
              <MobileMonthScroller
                currentDate={currentDate}
                eventMap={eventMap}
                selectedDate={dayViewDate}
                onSelectDay={(date) => {
                  setDayViewDate(date)
                  setActiveFilter('Day')
                }}
                onChangeCurrentDate={(date) => setCurrentDate(date)}
              />
            )}

            {isMobile && !needsCalendarConnection && activeFilter === 'Day' && (
                <div className="mobile-day-view">
                  <div className="mobile-day-view-header">
                    <h3>{dayViewDate ? format(dayViewDate, 'MMMM d, yyyy') : 'Day'}</h3>
                    <div className="mobile-day-view-actions" />
                  </div>
                <div
                  onContextMenu={(e) => {
                    e.preventDefault()
                    const anchor = { x: e.clientX, y: e.clientY + window.scrollY }
                    const selectedDate = new Date(dayViewDate || currentDate)
                    setCurrentDate(selectedDate)
                    setDayViewDate(selectedDate)
                    lastPointerRef.current = anchor
                    setContextMenu({ x: anchor.x, y: anchor.y, date: selectedDate })
                  }}
                >
                  <CalendarDaySchedule
                    baseDate={dayViewDate || currentDate}
                    items={
                      dayViewDate
                        ? calendarEvents.flatMap((evt) =>
                            isSameDay(evt.start, dayViewDate)
                              ? [{ id: String(evt.id), title: evt.title, start: evt.start.toISOString(), end: evt.end.toISOString(), tags: evt.tags }]
                              : []
                          )
                        : []
                    }
                    onCreateRequest={handleCreateRequest}
                    onSelect={(id, rect) => {
                      setSelectedEventId(id)
                      setAnchorEl(null)
                      if (rect) {
                        setAnchorCenter({
                          x: rect.left + rect.width / 2,
                          y: rect.top + window.scrollY,
                        })
                      } else {
                        setAnchorCenter({
                          x: window.innerWidth / 2,
                          y: window.scrollY + 200,
                        })
                      }
                      requestAnimationFrame(() => updatePositionFromAnchor())
                    }}
                  />
                </div>
              </div>
            )}

            {selectedEvent && (activeFilter === 'Month' || activeFilter === 'Week' || activeFilter === 'Day') && (
              <div
                className="modal-backdrop"
                onClick={() => {
                  setSelectedEventId(null)
                  setDraftEvent(null)
                  setDateClients((prev) => {
                    const next = new Map(prev)
                    next.delete('new')
                    return next
                  })
                }}
              >
                <div
                  ref={modalRef}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    top: modalStyle?.top ?? window.scrollY + 120,
                    left: modalStyle?.left ?? window.innerWidth / 2,
                    transform: 'translate(0, 0)',
                  }}
                >
                    <CalendarEventPopover
                      event={selectedEvent}
                      tailLeft={tailLeft}
                      tailPlacement={tailPlacement}
                      titleValue={draftEvent?.title ?? draftTitle}
                    locationValue={draftEvent?.location ?? draftLocation}
                    clients={selectedEventClients}
                    onSave={handleSaveEventCS}
                    onArchive={isDraft ? undefined : handleDeleteEventCS}
                    onTitleChange={(title) => {
                      setDraftTitle(title)
                      setDraftEvent((prev) =>
                        prev ? { ...prev, title } : prev,
                      )
                    }}
                    onLocationChange={(loc) => {
                      setDraftLocation(loc)
                      setDraftEvent((prev) => (prev ? { ...prev, location: loc } : prev))
                    }}
                    onAddClient={canManageContacts ? () => setShowContactPicker(true) : undefined}
                    onCancel={() => {
                      setSelectedEventId(null)
                      setDraftEvent(null)
                      setDateClients((prev) => {
                        const next = new Map(prev)
                        next.delete('new')
                        return next
                      })
                    }}
                  />
        <ConversationContactPicker
          open={showContactPicker}
          conversationId={selectedEvent?.conversation_id || ''}
          dateId={selectedEvent?.id && selectedEvent.id !== 'new' ? selectedEvent.id : undefined}
          linkedClientIds={new Set(selectedEventClients.map((c) => c.id))}
          onClose={() => setShowContactPicker(false)}
          onAdded={(contact) => {
            if (selectedEvent?.id) {
              // optimistic add
              setDateClients((prev) => {
                const next = new Map(prev)
                const existing = next.get(selectedEvent.id) || []
                next.set(selectedEvent.id, [...existing, contact])
                return next
              })
              if (selectedEvent.id !== 'new') {
                // refresh from server to confirm persistence
                fetchDateClients(selectedEvent.id)
              }
            }
            setShowContactPicker(false)
          }}
          onRemoved={(clientId) => {
            if (selectedEvent?.id) {
              setDateClients((prev) => {
                const next = new Map(prev)
                const existing = next.get(selectedEvent.id) || []
                next.set(
                  selectedEvent.id,
                  existing.filter((c) => c.id !== clientId),
                )
                return next
              })
              if (selectedEvent.id !== 'new') {
                fetchDateClients(selectedEvent.id)
              }
            }
            setShowContactPicker(false)
          }}
          sessionUserId={userId}
          size="wide"
        />
                  {(saveError || archiveError) && (
                    <div className="muted" style={{ color: '#b42318', marginTop: 6 }}>
                      {saveError || archiveError}
                    </div>
                  )}
                </div>
              </div>
            )}
            {contextMenu && (
              <div
                className="context-menu"
                style={{
                  position: 'absolute',
                  top: contextMenu.y,
                  left: contextMenu.x,
                }}
              >
                <button
                  type="button"
                  className="context-menu-item"
                  onClick={() => {
                    const baseDate = contextMenu.date ? new Date(contextMenu.date) : new Date()
                    const start = new Date(baseDate)
                    start.setHours(12, 0, 0, 0)
                    setCurrentDate(start)
                    setDayViewDate(start)
                    const end = new Date(start.getTime() + 60 * 60 * 1000)
                    const startIso = start.toISOString()
                    const endIso = end.toISOString()
                    setDateClients((prev) => {
                      const next = new Map(prev)
                      next.set('new', [])
                      return next
                    })
                    setDraftEvent({
                      id: 'new',
                      title: draftTitle,
                      datetime_start: startIso,
                      datetime_end: endIso,
                      date_created: startIso,
                      location: draftLocation,
                      parsedStart: start,
                      parsedEnd: end,
                    })
                    setSelectedEventId('new')
                    setAnchorEl(null)
                    setAnchorCenter(lastPointerRef.current || { x: contextMenu.x, y: contextMenu.y })
                    setTailLeft('50%')
                    setModalStyle(null)
                    setContextMenu(null)
                    requestAnimationFrame(() => updatePositionFromAnchor())
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  Create new event
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default CalendarView
